import { IconButton } from '@affine/component';
import { useActiveBlocksuiteEditor } from '@affine/core/components/hooks/use-block-suite-editor';
import { Bound } from '@blocksuite/affine/global/gfx';
import { DocModeProvider } from '@blocksuite/affine/shared/services';
import { CloseIcon, MindmapIcon } from '@blocksuite/icons/rc';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { findCardNotes, NODE_TYPE_REGISTRY } from '../handlers/canvas-helpers';
import {
  MINDMAP_CHANGED_EVENT,
  MINDMAP_NODE_TYPES,
  type MindmapNodeType,
} from '../types';
import * as styles from './mindmap-outline-panel.css';

interface CardSummary {
  noteId: string;
  type: MindmapNodeType | null;
  preview: string;
  xywh: string;
}

/**
 * Right-rail floating panel that lists every Research-Canvas card on
 * the active doc, grouped by type. Each row is the first line of the
 * card's quote, prefixed by its type chip. Clicking a row switches
 * the editor to edgeless and pans the viewport to the card so the
 * researcher can see + edit it without losing their place in the
 * source doc.
 */
export function MindmapOutlinePanel() {
  const [activeEditor] = useActiveBlocksuiteEditor();
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onChange = () => setTick(t => t + 1);
    window.addEventListener(MINDMAP_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(MINDMAP_CHANGED_EVENT, onChange);
  }, []);

  // Subscribe to BlockSuite surface/store updates so the outline reflects
  // manual drags and canvas-side edits instantly. Programmatic adds still
  // arrive via MINDMAP_CHANGED_EVENT (above); this covers everything the
  // custom event can't see — without the old 2s polling latency.
  useEffect(() => {
    if (!open) return;
    const store = activeEditor?.host?.std?.store as
      | {
          slots?: {
            blockUpdated?: {
              subscribe: (cb: () => void) => { unsubscribe: () => void };
            };
          };
        }
      | undefined;
    const slot = store?.slots?.blockUpdated;
    if (!slot) return;
    const disposable = slot.subscribe(() => setTick(t => t + 1));
    return () => disposable.unsubscribe();
  }, [open, activeEditor]);

  const cards = useMemo<CardSummary[]>(() => {
    void tick;
    const doc = activeEditor?.host?.std?.store;
    if (!doc) return [];
    try {
      const notes = findCardNotes(doc);
      return notes.map(note => readCardSummary(doc, note));
    } catch (err) {
      console.debug('[canvas-outline] read failed — will retry', err);
      return [];
    }
  }, [activeEditor, tick]);

  const grouped = useMemo(() => {
    const byType: Record<string, CardSummary[]> = {};
    for (const c of cards) {
      const key = c.type ?? 'other';
      (byType[key] ??= []).push(c);
    }
    return byType;
  }, [cards]);

  const handleJumpToCard = useCallback(
    (card: CardSummary) => {
      const editor = activeEditor;
      const host = editor?.host;
      if (!host) return;
      try {
        host.std.get(DocModeProvider).setEditorMode('edgeless');
      } catch {
        /* mode service unavailable */
      }
      // Wait one paint so the edgeless renderer mounts, then pan to
      // the card's bound. We grab the gfx controller via the std DI.
      window.setTimeout(() => {
        try {
          const bound = Bound.deserialize(card.xywh);
          const gfx = host.std.get(
            // GfxControllerIdentifier symbol is module-loaded by
            // BlockSuite; we look it up by string in the std map. If
            // the symbol isn't there yet (slow mount) we silently
            // skip — the user is at least already in edgeless.
            host.std.provider.constructor as never
          ) as
            | { viewport?: { setViewportByBound?: (b: Bound) => void } }
            | undefined;
          if (gfx?.viewport?.setViewportByBound) {
            // Expand by a margin so the card isn't flush against the edges.
            const padded = new Bound(
              bound.x - 80,
              bound.y - 80,
              bound.w + 160,
              bound.h + 160
            );
            gfx.viewport.setViewportByBound(padded);
          }
        } catch {
          /* pan failed — user can still scroll manually */
        }
      }, 120);
    },
    [activeEditor]
  );

  // Hide the toggle entirely when there's nothing to show —
  // either no doc is open (All Docs list, settings) or the active
  // doc has zero canvas cards. The button only appears once the
  // researcher has actually added something to the canvas.
  if (cards.length === 0) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={styles.toggle}
        data-open={open ? 'true' : undefined}
        onClick={() => setOpen(o => !o)}
        title={open ? 'Hide canvas outline' : 'Show canvas outline'}
        aria-label="Toggle canvas outline panel"
      >
        <MindmapIcon />
      </button>

      {open && (
        <aside className={styles.panel} aria-label="Canvas outline">
          <header className={styles.header}>
            <span className={styles.headerTitle}>
              Canvas outline · {cards.length} card
              {cards.length === 1 ? '' : 's'}
            </span>
            <IconButton
              size="20"
              variant="plain"
              onClick={() => setOpen(false)}
              aria-label="Close canvas outline"
            >
              <CloseIcon />
            </IconButton>
          </header>

          <ol className={styles.tree}>
            {MINDMAP_NODE_TYPES.map(type => {
              const list = grouped[type];
              if (!list?.length) return null;
              const meta = NODE_TYPE_REGISTRY[type];
              return (
                <li key={type} className={styles.row}>
                  <div
                    className={styles.rowLabel}
                    data-depth="0"
                    style={{ borderLeft: `3px solid ${meta.strokeColor}` }}
                  >
                    {meta.branchLabel} · {list.length}
                  </div>
                  <ol className={styles.children}>
                    {list.map(card => (
                      <li key={card.noteId} className={styles.row}>
                        <button
                          type="button"
                          className={styles.rowLabel}
                          data-clickable="true"
                          data-depth="1"
                          onClick={() => handleJumpToCard(card)}
                        >
                          {card.preview || '(empty card)'}
                        </button>
                      </li>
                    ))}
                  </ol>
                </li>
              );
            })}
            {grouped['other']?.length ? (
              <li className={styles.row}>
                <div className={styles.rowLabel} data-depth="0">
                  Other · {grouped['other'].length}
                </div>
                <ol className={styles.children}>
                  {grouped['other'].map(card => (
                    <li key={card.noteId} className={styles.row}>
                      <button
                        type="button"
                        className={styles.rowLabel}
                        data-clickable="true"
                        data-depth="1"
                        onClick={() => handleJumpToCard(card)}
                      >
                        {card.preview || '(empty card)'}
                      </button>
                    </li>
                  ))}
                </ol>
              </li>
            ) : null}
          </ol>
        </aside>
      )}
    </>
  );
}

/**
 * Pull the type chip (first paragraph, formatted **TYPE**) and a one-
 * line preview from the card's child paragraphs. Tolerant of cards
 * whose body the user has rewritten — falls back to "(empty card)" if
 * we can't find a quote line.
 */
function readCardSummary(
  doc: { getBlock: (id: string) => { model?: unknown } | undefined },
  note: unknown
): CardSummary {
  const model = note as {
    id: string;
    children?: Array<{ id: string }>;
    props?: { xywh?: string };
  };
  const xywh = model.props?.xywh ?? '[0,0,0,0]';
  let type: MindmapNodeType | null = null;
  let preview = '';
  const children = model.children ?? [];
  for (const child of children) {
    const block = doc.getBlock(child.id)?.model as
      | {
          flavour?: string;
          text?: { toString(): string };
          props?: { type?: string; text?: { toString(): string } };
        }
      | undefined;
    if (!block) continue;
    const text = (block.text ?? block.props?.text)?.toString().trim();
    if (!text) continue;
    if (!type) {
      // First paragraph is the type chip — match the registry's label.
      const upper = text.toUpperCase();
      for (const t of MINDMAP_NODE_TYPES) {
        if (upper.startsWith(NODE_TYPE_REGISTRY[t].label.toUpperCase())) {
          type = t;
          break;
        }
      }
      if (type) continue;
    }
    if (!preview) {
      preview = text.length > 80 ? `${text.slice(0, 80)}\u2026` : text;
    }
    if (type && preview) break;
  }
  return { noteId: model.id, type, preview, xywh };
}
